import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import useMidnightCountdown, { formatCountdownClock } from "../../hooks/useMidnightCountdown";
import { formatFeaturedLongDate } from "../../utils/featuredDisplay";
import {
  fetchNominationLeaderboard,
  fetchPlatformFeatured,
  resolveFeaturedCreators,
} from "../../services/featuredService";
import {
  FeaturedGridCard,
  FeaturedHeroCard,
} from "../../components/featured/FeaturedCreatorCards";
import NominationLeaderboard from "../../components/featured/NominationLeaderboard";
import FeaturedCategoryToggle from "../../components/featured/FeaturedCategoryToggle";
import "./FeaturedCategory.css";

const MODE_CONFIG = {
  discovery: {
    eyebrow: "Discovered Tonight",
    title: "Tonight's",
    titleEm: "Discovery",
    hand: "pure random — no nominations, just emerging voices",
    subtitle:
      "5 creators chosen at pure random every midnight. Any creator with an open drop and under 10K followers is eligible.",
    idsKey: "discoveryCreators",
    includeNominationCounts: false,
  },
  community: {
    eyebrow: "Community's Choice",
    title: "Community's",
    titleEm: "Choice",
    hand: "chosen by weighted lottery from fan nominations",
    subtitle:
      "5 creators chosen by a weighted nomination lottery. More nominations mean a higher chance — never a guarantee.",
    idsKey: "communityCreators",
    includeNominationCounts: true,
  },
};

export default function FeaturedCategoryPage({ mode = "discovery" }) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.discovery;
  const remainingMs = useMidnightCountdown();
  const clock = formatCountdownClock(remainingMs);

  const [loading, setLoading] = useState(true);
  const [creators, setCreators] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const platform = await fetchPlatformFeatured();
        const ids = platform[config.idsKey] || [];
        const previewKey =
          config.idsKey === "discoveryCreators"
            ? "discoveryPreview"
            : "communityPreview";
        const previewFallback = platform[previewKey] || [];

        const resolved = await resolveFeaturedCreators(ids, {
          includeNominationCounts: config.includeNominationCounts,
          previewFallback,
        });

        let board = [];
        if (mode === "community") {
          board = await fetchNominationLeaderboard(8);
        }

        if (!cancelled) {
          setCreators(resolved);
          setLeaderboard(board);
        }
      } catch (err) {
        console.error(`Featured ${mode} load error:`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [mode, config.idsKey, config.includeNominationCounts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("ft-reveal--visible");
        });
      },
      { threshold: 0.12 }
    );

    document.querySelectorAll(".ft-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, creators]);

  const hero = creators[0];
  const rowOne = creators.slice(1, 3);
  const rowTwo = creators.slice(3, 5);
  const todayLabel = formatFeaturedLongDate(new Date());

  return (
    <div className={`ft-category ft-category--${mode}`}>
      <header className="ft-category-top">
        <Link to="/featured" className="ft-category-back">
          ← Featured home
        </Link>
        <div className="ft-category-countdown" aria-live="polite">
          <span className="ft-category-countdown-label">Next rotation in</span>
          <span className="ft-category-countdown-time">
            <span>{clock.h}</span>h <span>{clock.m}</span>m <span>{clock.s}</span>s
          </span>
        </div>
      </header>

      <section className="ft-category-intro">
        <FeaturedCategoryToggle activeMode={mode} />
        <span className="ft-category-eyebrow ft-reveal">{config.eyebrow}</span>
        <h1 className="ft-category-title ft-reveal">
          {config.title} <em>{config.titleEm}</em>
        </h1>
        <span className="ft-category-hand ft-reveal">{config.hand}</span>
        <p className="ft-category-sub ft-reveal">{config.subtitle}</p>
        <div className="ft-category-date ft-reveal">{todayLabel}</div>
      </section>

      {loading ? (
        <div className="ft-category-loading">Loading tonight&apos;s featured creators…</div>
      ) : creators.length === 0 ? (
        <div className="ft-category-empty">
          <p>No featured creators yet for tonight.</p>
          <Link to="/gallery" className="ft-category-empty-link">
            Browse the Gallery →
          </Link>
        </div>
      ) : (
        <section className="ft-category-grid-wrap">
          {hero && (
            <div className="ft-reveal">
              <FeaturedHeroCard creator={hero} mode={mode} />
            </div>
          )}

          {rowOne.length > 0 && (
            <div className="ft-two-col-grid">
              {rowOne.map((c) => (
                <div key={c.id} className="ft-reveal">
                  <FeaturedGridCard creator={c} mode={mode} />
                </div>
              ))}
            </div>
          )}

          {rowTwo.length > 0 && (
            <div className="ft-two-col-grid">
              {rowTwo.map((c) => (
                <div key={c.id} className="ft-reveal">
                  <FeaturedGridCard creator={c} mode={mode} />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {mode === "community" && !loading && (
        <NominationLeaderboard rows={leaderboard} />
      )}

      <section className="ft-category-how">
        <div className="ft-category-how-grid">
          <div className="ft-category-how-card ft-reveal">
            <div className="ft-category-how-num">01</div>
            <div className="ft-category-how-title">
              {mode === "discovery" ? "Pure random at midnight" : "Community nominates"}
            </div>
            <p className="ft-category-how-desc">
              {mode === "discovery"
                ? "Five emerging creators are chosen at random from eligible accounts with open drops."
                : "Fans spend credits to nominate creators they believe in. Each nomination adds weight to the lottery."}
            </p>
          </div>
          <div className="ft-category-how-card ft-reveal">
            <div className="ft-category-how-num">02</div>
            <div className="ft-category-how-title">24 hours in the spotlight</div>
            <p className="ft-category-how-desc">
              Featured creators appear here and in the nightly archive. Past featured days are
              preserved forever.
            </p>
          </div>
          <div className="ft-category-how-card ft-reveal">
            <div className="ft-category-how-num">03</div>
            <div className="ft-category-how-title">30 day cool-down</div>
            <p className="ft-category-how-desc">
              Once featured, a creator cannot be selected again for 30 days — keeping the honour
              meaningful for everyone.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export function FeaturedDiscoveryPage() {
  return <FeaturedCategoryPage mode="discovery" />;
}

export function FeaturedCommunityPage() {
  return <FeaturedCategoryPage mode="community" />;
}
